import React, { useState, useEffect } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { FilterIcon, SortIcon } from '@components/icons/svg-icon';
import { SortOrder } from '@models/table.model';

interface FilterOption {
  value: string | SortOrder;
  title: string;
  column: string;
}

interface MediaLibraryFilterProps {
  onFilterChange?: (data: {
    value: string | SortOrder;
    mode: 'filter' | 'sort';
    column: string;
  }) => void;
  value?: string | SortOrder;
  column?: string;
  mode?: 'filter' | 'sort';
  dataOptions?: FilterOption[];
}

const MediaLibraryFilter: React.FC<MediaLibraryFilterProps> = ({
  onFilterChange,
  value,
  column,
  mode = 'filter',
  dataOptions = []
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const initialOption =
    dataOptions.find(
      (o) => o.value === value && (!column || o.column === column)
    ) || dataOptions[0];

  const [selectedValue, setSelectedValue] = useState(initialOption.value);
  const [selectedColumn, setSelectedColumn] = useState(initialOption.column);

  useEffect(() => {
    const option =
      dataOptions.find(
        (o) => o.value === value && (!column || o.column === column)
      ) || dataOptions[0];
    setSelectedValue(option.value);
    setSelectedColumn(option.column);
  }, [value, column, dataOptions]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (option: FilterOption) => {
    setSelectedValue(option.value);
    setSelectedColumn(option.column);
    onFilterChange?.({ value: option.value, column: option.column, mode });
    setAnchorEl(null);
  };

  const selectedOption =
    dataOptions.find(
      (o) => o.value === selectedValue && o.column === selectedColumn
    ) || dataOptions[0];

  return (
    <>
      <div
        className="cursor-pointer flex items-center gap-1.5 select-none"
        onClick={handleClick}
      >
        {mode === 'sort' ? (
          <SortIcon
            className="!w-[18px] !h-[18px] transition-transform duration-200"
            style={{
              transform:
                selectedOption.value === SortOrder.Ascending
                  ? 'rotate(0deg)'
                  : 'rotate(180deg)'
            }}
          />
        ) : (
          <FilterIcon className="!w-[18px] !h-[18px]" />
        )}
        <span className="font-medium text-[var(--blue-4)] text-base">
          {selectedOption.title}
        </span>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        disableEnforceFocus
        disableRestoreFocus
      >
        {dataOptions.map((option) => (
          <MenuItem
            key={`${option.column}-${option.value}`}
            selected={
              option.value === selectedValue && option.column === selectedColumn
            }
            onClick={() => handleSelect(option)}
          >
            {option.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MediaLibraryFilter;
